import type { Capabilities } from '@/api/types'
import { useSessionStore } from './session-store'

/** `<resource>:<verb>` as the registry spells it. */
export type Permission = string

export function hasPermission(capabilities: Capabilities | null, permission: Permission): boolean {
  return capabilities?.permissions.includes(permission) ?? false
}

export function hasAllPermissions(
  capabilities: Capabilities | null,
  required: readonly Permission[],
): boolean {
  return required.every((permission) => hasPermission(capabilities, permission))
}

export function hasAnyPermission(
  capabilities: Capabilities | null,
  required: readonly Permission[],
): boolean {
  return required.some((permission) => hasPermission(capabilities, permission))
}

/**
 * ADR-029: a location-scoped role with no assigned locations is denied everywhere — including
 * `GET /capabilities` (403 `location_out_of_scope`). The session service records that refusal as
 * `capabilitiesDenied`; no role name is consulted anywhere.
 */
export function useLockedOut(): boolean {
  return useSessionStore((state) => state.status === 'authenticated' && state.capabilitiesDenied)
}

/** React hook: does the current session hold `permission`? */
export function useCan(permission: Permission): boolean {
  return useSessionStore((state) => hasPermission(state.capabilities, permission))
}

export function useCanAll(required: readonly Permission[]): boolean {
  return useSessionStore((state) => hasAllPermissions(state.capabilities, required))
}

export function useCapabilities(): Capabilities | null {
  return useSessionStore((state) => state.capabilities)
}
